import React from 'react';
import PropTypes from 'prop-types';
import { PROMOTION_SCORE, formatScore, scoreTone } from './predictWatchlistModel';
import './predictPages.css';

// Shared pieces of Predict's dashboard, bug page and Model and scoring page
// (STRY0039090). Tones follow the watch list's score chips.

const TONE_STYLE = {
  bad: { background: 'var(--bad-soft)', color: 'var(--bad)' },
  prevent: { background: 'var(--prevent-soft)', color: 'var(--prevent-ink)' },
  warn: { background: 'var(--warn-soft)', color: 'var(--warn)' },
  muted: { background: 'var(--line-soft)', color: 'var(--ink-soft)' }
};

export const ScoreBadge = ({ score }) => (
  <span
    className="bz-mono"
    style={{
      display: 'inline-flex',
      justifyContent: 'center',
      minWidth: 40,
      padding: '2px 7px',
      borderRadius: 'var(--r-sm)',
      fontWeight: 600,
      fontSize: 'var(--fs-md)',
      ...TONE_STYLE[scoreTone(score)]
    }}
  >
    {formatScore(score)}
  </span>
);
ScoreBadge.propTypes = { score: PropTypes.number };

const isScore = score => typeof score === 'number' && Number.isFinite(score);

// Where a score sits along 0-10, 0 to 100.
const along = score => (isScore(score) ? Math.min(100, Math.max(0, score * 10)) : 0);

// A mark this far along puts its label on its left, inside the meter.
const END_OF_SCALE = 75;

const MeterMark = ({ kind, at, children }) => (
  <div
    className={`pdp-meter__mark pdp-meter__mark--${kind}${along(at) > END_OF_SCALE ? ' pdp-meter__mark--end' : ''}`}
    style={{ left: `${along(at)}%` }}
  >
    <span>{children}</span>
  </div>
);
MeterMark.propTypes = { kind: PropTypes.string.isRequired, at: PropTypes.number.isRequired, children: PropTypes.node };

// The score on 0-10, with 6.0 (where every bug is promoted) and the customer's
// threshold marked. Read out as one sentence.
export const ScoreMeter = ({ score, threshold }) => {
  const yours = isScore(threshold) ? `; your threshold is ${formatScore(threshold)}` : '';
  return (
    <div
      className="pdp-meter"
      role="img"
      data-testid="score-meter"
      aria-label={`Score ${formatScore(score)} of 10. Bugs are promoted at ${formatScore(PROMOTION_SCORE)}${yours}.`}
    >
      <div className="pdp-meter__fill" style={{ width: `${along(score)}%` }} />
      <MeterMark kind="promoted" at={PROMOTION_SCORE}>
        {formatScore(PROMOTION_SCORE)} promoted
      </MeterMark>
      {isScore(threshold) && (
        <MeterMark kind="yours" at={threshold}>
          Yours {formatScore(threshold)}
        </MeterMark>
      )}
    </div>
  );
};
ScoreMeter.propTypes = { score: PropTypes.number, threshold: PropTypes.number };

export const StatTile = ({ label, value, sub, testId }) => (
  <div className="bz-stat" role="group" aria-label={label} style={{ borderTop: '3px solid var(--predict)' }}>
    <div className="bz-stat__label" aria-hidden="true">
      {label}
    </div>
    <div className="bz-stat__value" data-testid={testId}>
      {value}
    </div>
    {sub && <div className="bz-stat__meta">{sub}</div>}
  </div>
);
StatTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  sub: PropTypes.node,
  testId: PropTypes.string
};

export const PredictCard = ({ title, right, children, labelledBy }) => (
  // A div, not a <section>: the theme pads every section (_reboot.scss).
  <div className="bz-card" role="region" aria-labelledby={labelledBy}>
    <div className="pdp-card-head">
      <h2 id={labelledBy} className="bz-h3" style={{ margin: 0 }}>
        {title}
      </h2>
      {right}
    </div>
    {children}
  </div>
);
PredictCard.propTypes = {
  title: PropTypes.node.isRequired,
  right: PropTypes.node,
  children: PropTypes.node,
  labelledBy: PropTypes.string.isRequired
};

export const PageHead = ({ kicker, title, sub, children }) => (
  <div className="bz-section-head" style={{ marginBottom: 0, flexWrap: 'wrap', gap: 12 }}>
    <div style={{ minWidth: 0 }}>
      <div className="bz-kicker" style={{ color: 'var(--predict)' }}>
        {kicker}
      </div>
      <h1 className="bz-h1" style={{ marginTop: 4, overflowWrap: 'anywhere' }}>
        {title}
      </h1>
      {sub && (
        <div className="bz-muted" style={{ fontSize: 'var(--fs-md)', marginTop: 4 }}>
          {sub}
        </div>
      )}
    </div>
    {children && (
      <div className="bz-row" style={{ gap: 8, flexWrap: 'wrap' }}>
        {children}
      </div>
    )}
  </div>
);
PageHead.propTypes = {
  kicker: PropTypes.node,
  title: PropTypes.node.isRequired,
  sub: PropTypes.node,
  children: PropTypes.node
};
